import { connect } from "react-redux";
import * as React from "react";
import areEqual from "fbjs/lib/areEqual";

import { AbsoluteModelPath } from "../../../../../../../Common/ModelPath/ModelPath";
import { ViewModelSelect } from "../../../../../Helpers/StateAccessors/ViewModelSelect";
import { FullFormState } from "../../../../../Engine/Core/Store/FullFormState";
import { withViewModelSelect } from "../../../../withViewModelSelect";
import { UnitId } from "../../../Unit/UnitId/UnitId";
import { IterUtils } from "../../../../../../../Common/IterableUtils";
import { StringArrayKey } from "../../../../../../../Common/StringArrayKey";
import { UnitItemExtraInfoPath, UnitLabelPath } from "../../../../../../../Common/PathConstants";
import { CandyDispatch } from "../../../../../Engine/Core/Store/CandyReduxTypes";
import { setToLocalStorage } from "../../../../../Engine/Core/Store/LocalStorageUtils";
import { getLabel, getLabelForModal, getMenuItemInfosKey } from "../../UnitItemHelpers";

import { MultipleUnit } from "./MultipleUnit";

export interface MultipleUnitContainerProps {
    id: UnitId;
    childUnitIds: UnitId[];
    unitPath: AbsoluteModelPath;
    viewModelSelect: ViewModelSelect;
    onRemoveInstance: (instance: string, instanceTitle: string) => void;
    onClearInstance: (instance: string, instanceTitle: string) => void;
    children?: React.ReactNode;
    maxOccurs?: number;
    name?: string;
}

const mapDispatchToProps = (_: CandyDispatch, ownProps: MultipleUnitContainerProps) => ({
    setCollapsedUnitItem: (isCollapsed: boolean, fufDraftId?: string, instance?: string, instances?: string[]) => {
        setToLocalStorage<boolean>(
            getMenuItemInfosKey(ownProps.id.pageId, fufDraftId, instance, instances, ownProps.name),
            isCollapsed
        );
    },
});

const mapStateToProps = (state: FullFormState, ownProps: MultipleUnitContainerProps) => {
    const instances = ownProps.viewModelSelect.children(state, ownProps.unitPath);
    const filled = instances.length > 0;

    const hasSingleChild = instances.length <= 1 && !ownProps.children;

    const currentUnit = state.highlightUnit;
    const unitId = ownProps.id;
    let isChildItemActive = false;
    let currentInstance: string | undefined;
    if (currentUnit != undefined) {
        if (
            ownProps.id.pageId === currentUnit.pageId &&
            areEqual(unitId.parentInstances, currentUnit.parentInstances)
        ) {
            isChildItemActive = true;
            currentInstance = currentUnit.instance;
        } else if (ownProps.childUnitIds.some(child => child.pageId === currentUnit.pageId)) {
            isChildItemActive = true;
            currentInstance = IterUtils.last(currentUnit.parentInstances ?? []);
        } else {
            const key = new StringArrayKey([unitId.pageId, ...(unitId.parentInstances ?? [])]);
            const prevHighlightedInstance = state.highlightUnitsInfo?.get(key);
            currentInstance = prevHighlightedInstance;
        }
    }

    const getInstanceTitle = (instance: string): string =>
        getLabel(
            ownProps.viewModelSelect.customFieldValue(
                state,
                ownProps.unitPath.joinWith(instance).joinWith(UnitLabelPath),
                "value"
            )
        );

    const getInstanceTitleForModal = (instance: string, instanceIndex: number | string): string =>
        getLabelForModal(
            ownProps.viewModelSelect.customFieldValue(
                state,
                ownProps.unitPath.joinWith(instance).joinWith(UnitLabelPath),
                "value"
            ),
            Number(instanceIndex)
        );

    const getInstanceExtraInfo = (instance: string): string | undefined =>
        ownProps.viewModelSelect.customFieldValue(
            state,
            ownProps.unitPath.joinWith(instance).joinWith(UnitItemExtraInfoPath),
            "value"
        );

    const errorsCount = Number(ownProps.viewModelSelect.customFieldValue(state, ownProps.unitPath, "errorsCount", ""));

    return {
        currentInstance: currentInstance,
        parentInstances: unitId.parentInstances,
        isChildItemActive: isChildItemActive,
        instances: instances,
        filled: filled,
        visible: state.settings.isReadOnly ? filled : true,
        isReadOnly: state.settings.isReadOnly,
        hasSingleChild: hasSingleChild,
        fufDraftId: state.settings.draftId,
        errorsCount: errorsCount,
        onRemoveInstance: (instance: string, instanceIndex: number) =>
            ownProps.onRemoveInstance(instance, getInstanceTitleForModal(instance, instanceIndex)),
        onClearInstance: (instance: string, instanceTitle: string) =>
            ownProps.onClearInstance(instance, getInstanceTitleForModal(instance, instanceTitle)),
        getInstanceTitle: getInstanceTitle,
        getInstanceExtraInfo: getInstanceExtraInfo,
    };
};

export const MultipleUnitContainer = withViewModelSelect(connect(mapStateToProps, mapDispatchToProps)(MultipleUnit));
